// ── Client Action Registry ───────────────────────────────────
// Self-describing, composable action handlers that replace the
// monolithic handleChatAction if-chain. Each action registers
// a handler that receives (params, ctx) where ctx provides
// access to app state and callbacks.

const handlers = new Map();

// ── registerAction: bind a handler to an action id ───────────

export function registerAction(id, handler) {
  handlers.set(id, handler);
}

// ── executeActions: dispatch an actions JSON block ────────────
// Returns array of action cards produced.

export function executeActions(actions, ctx) {
  if (!actions) return [];
  const cards = [];

  const pushCard = (action, label, detail, buttons) => {
    cards.push({ type: 'action_card', action, label, detail, buttons, timestamp: Date.now() });
  };

  for (const [actionId, params] of Object.entries(actions)) {
    const handler = handlers.get(actionId);
    if (!handler) continue;
    try {
      handler(params, { ...ctx, pushCard });
    } catch (err) {
      console.error(`Action "${actionId}" failed:`, err);
      pushCard(actionId, `${actionId} failed`, err.message, []);
    }
  }

  return cards;
}

// ── Helpers shared across action handlers ────────────────────

export function applyScope(actionVal, setChatFilter) {
  if (actionVal && typeof actionVal === 'object' && !Array.isArray(actionVal)) {
    const { types, nodeIds } = actionVal;
    if (types?.length || nodeIds?.length) {
      setChatFilter({
        types: types?.length ? types : undefined,
        nodeIds: nodeIds?.length ? nodeIds : undefined,
      });
    }
  }
}

export function getScopedNodes(actionVal, allNodes) {
  if (!actionVal || typeof actionVal !== 'object' || actionVal === true) return allNodes;
  const { types, nodeIds } = actionVal;
  if (!types?.length && !nodeIds?.length) return allNodes;
  return allNodes.filter(n => {
    const t = n.data?.type || n.type;
    const matchType = types?.length ? types.includes(t) : true;
    const matchId = nodeIds?.length ? nodeIds.includes(n.id) : true;
    return (types?.length && nodeIds?.length) ? (matchType || matchId) : (matchType && matchId);
  });
}

export function getSubtreeNodes(rootId, allNodes) {
  const subtreeIds = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift();
    for (const node of allNodes) {
      const d = node.data || node;
      const parentIds = d.parentIds || (d.parentId ? [d.parentId] : []);
      if (parentIds.includes(id) && !subtreeIds.has(node.id)) {
        subtreeIds.add(node.id);
        queue.push(node.id);
      }
    }
  }
  return allNodes.filter(n => subtreeIds.has(n.id));
}

// ── Register all built-in action handlers ────────────────────

// --- Viewing & Filtering ---

registerAction('filter', (params, ctx) => {
  const { types, nodeIds } = params;
  ctx.setChatFilter({
    types: types?.length ? types : undefined,
    nodeIds: nodeIds?.length ? nodeIds : undefined,
  });
});

registerAction('clear', (_params, ctx) => {
  ctx.setChatFilter(null);
});

registerAction('importClaudeMemory', (_params, ctx) => {
  ctx.setShowClaudeMemoryImport(true);
  ctx.pushCard('importClaudeMemory', 'Claude Memory', 'Review local Claude Code and Cowork history before importing', []);
});

registerAction('startMode', (params, ctx) => {
  const allowed = new Set(['idea', 'decision', 'writing', 'plan', 'learn', 'codebase', 'resume']);
  const mode = allowed.has(params?.mode) ? params.mode : 'idea';
  const goal = typeof params?.goal === 'string' ? params.goal.trim() : '';
  ctx.handleNextModeSelect(mode, goal);
  ctx.pushCard('startMode', 'Mission ready', `${mode}${goal ? ` · ${goal}` : ''}`, []);
});

// --- Creating Nodes ---

registerAction('addNodes', (params, ctx) => {
  if (!params?.length) return;
  const { active, buildFlowNode, upsertNode } = ctx;
  const existingDynConfig = active.rawNodesRef.current[0]?.data?.dynamicConfig || null;
  for (const nd of params) {
    const flowNode = buildFlowNode({
      id: nd.id || `chat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: nd.type || 'feature',
      label: nd.label,
      reasoning: nd.reasoning || '',
      parentId: nd.parentId || 'seed',
    });
    if (existingDynConfig) flowNode.data.dynamicConfig = existingDynConfig;
    active.rawNodesRef.current = upsertNode(active.rawNodesRef.current, flowNode);
  }
  active.applyLayout(active.rawNodesRef.current, active.drillStackRef.current);
  active.setNodeCount(active.rawNodesRef.current.length);
  ctx.pushCard('addNodes', `Added ${params.length} Nodes`, 'New nodes brainstormed onto canvas', []);
});

// --- Multi-Node Tools ---

registerAction('debate', (params, ctx) => {
  applyScope(params, ctx.setChatFilter);
  ctx.startDebateInChat();
});

registerAction('refine', (params, ctx) => {
  applyScope(params, ctx.setChatFilter);
  ctx.startRefineInChat(3);
});

registerAction('refineMore', (_params, ctx) => {
  const { setRefineStream, setShowChat, handleGoDeeper, setPendingChatCards } = ctx;
  setRefineStream({ status: 'critiquing', round: 1, maxRounds: 2 });
  setShowChat(true);
  const refineHistory = [];
  handleGoDeeper(2, (progress) => {
    if (progress.status === 'round_complete') {
      refineHistory.push({
        round: progress.round, oldScore: progress.oldScore, newScore: progress.newScore,
        newNodeCount: progress.newNodeCount, summary: progress.summary,
      });
    }
    if (progress.status === 'done') {
      setRefineStream(null);
      setPendingChatCards(prev => [...prev, { type: 'refine_card', state: { status: 'done', history: refineHistory } }]);
    } else {
      setRefineStream({ ...progress, history: refineHistory });
    }
  });
});

registerAction('portfolio', (params, ctx) => {
  applyScope(params, ctx.setChatFilter);
  const { active, setPortfolioFocus, startPortfolioInChat } = ctx;
  if (params && typeof params === 'object' && params !== true) {
    const scopedNodes = getScopedNodes(params, active.rawNodesRef.current);
    const nodeSummaries = scopedNodes.slice(0, 20).map(n => {
      const d = n.data || n;
      return `[${d.type}] ${d.label}: ${(d.reasoning || '').slice(0, 100)}`;
    });
    setPortfolioFocus({ types: params.types || null, nodeIds: params.nodeIds || null, nodeSummaries });
  } else {
    setPortfolioFocus(null);
  }
  startPortfolioInChat();
});

registerAction('portfolioMore', (_params, ctx) => {
  ctx.startPortfolioInChat();
});

registerAction('fractalExpand', (params, ctx) => {
  const rounds = params?.rounds || 3;
  ctx.setAutoFractalRounds(rounds);
  ctx.setShowAutoFractal(true);
  setTimeout(() => ctx.handleStartAutoFractal(), 100);
  ctx.pushCard('fractalExpand', 'Fractal Expanding', `Recursively exploring ${rounds} rounds...`, [
    { label: 'Open Panel', actionType: 'openPanel', panel: 'fractalExpand' },
  ]);
});

registerAction('scoreNodes', (params, ctx) => {
  const scopedNodes = getScopedNodes(params, ctx.active.rawNodesRef.current);
  ctx.triggerScoring(scopedNodes, ctx.ideaText);
  ctx.pushCard('scoreNodes', 'Scoring Nodes', `Evaluating ${scopedNodes.length} nodes...`, []);
});

// --- Single-Node Tools ---

registerAction('drill', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.active.handleDrill?.({ id: targetNode.id, data: targetNode.data || targetNode });
  }
});

registerAction('expandNode', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.active.handleFractalExpand?.(params.nodeId);
    ctx.pushCard('expandNode', 'Expanding Node', `Expanding "${(targetNode.data?.label || targetNode.id).slice(0, 40)}"...`, []);
  }
});

registerAction('editNode', (params, ctx) => {
  if (!params?.nodeId) return;
  const { nodeId, label, reasoning, type } = params;
  const updates = {};
  if (label !== undefined) updates.label = label;
  if (reasoning !== undefined) updates.reasoning = reasoning;
  if (type !== undefined) updates.type = type;
  if (Object.keys(updates).length > 0) {
    ctx.active.handleSaveNodeEdit?.(nodeId, updates);
    ctx.pushCard('editNode', 'Edited Node', `Updated ${Object.keys(updates).join(', ')}`, []);
  }
});

registerAction('regenerateNode', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.active.handleRegenerate?.(targetNode.id);
    ctx.pushCard('regenerateNode', 'Regenerating', `Re-generating "${(targetNode.data?.label || targetNode.id).slice(0, 40)}"`, []);
  }
});

registerAction('starNode', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.active.handleToggleStar?.(params.nodeId);
    ctx.pushCard('starNode', 'Toggled Star', `${(targetNode.data?.label || targetNode.id).slice(0, 40)}`, []);
  }
});

registerAction('deleteNode', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.nodeTools.handleRippleDelete(targetNode);
    ctx.pushCard('deleteNode', 'Deleted Node', 'Removed node and reparented children', []);
  }
});

registerAction('deleteBranch', (params, ctx) => {
  if (!params?.nodeId) return;
  const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === params.nodeId);
  if (targetNode) {
    ctx.nodeTools.handleDeleteBranch(targetNode);
    ctx.pushCard('deleteBranch', 'Deleted Branch', 'Removed node and all descendants', []);
  }
});

// --- Thinking Patterns ---

registerAction('executePattern', (params, ctx) => {
  if (!params?.patternId) return;
  const { patternId, nodeId } = params;
  const allNodes = ctx.active.rawNodesRef.current;
  const scopedNodes = nodeId ? getSubtreeNodes(nodeId, allNodes) : allNodes;
  ctx.patternExec$.execute(patternId, ctx.ideaText, scopedNodes, ctx.displayMode, { domain: ctx.dynamicDomain });
  ctx.setShowChat(true);
  ctx.pushCard('executePattern', `Running ${patternId}`, `Executing on ${scopedNodes.length} nodes`, []);
});

// --- Mode Tools ---

registerAction('buildPrototype', (_params, ctx) => {
  ctx.startPrototypeBuild();
  ctx.pushCard('buildPrototype', 'Building Prototype', 'Generating interactive prototype from tree...', []);
});

registerAction('feedToIdea', (params, ctx) => {
  const scopedNodes = getScopedNodes(params, ctx.active.rawNodesRef.current);
  const grouped = {};
  for (const n of scopedNodes) {
    const d = n.data || n;
    const type = d.type || 'node';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(`- ${d.label || n.id}${d.reasoning ? ': ' + d.reasoning : ''}`);
  }
  const summary = Object.entries(grouped)
    .map(([type, items]) => `## ${type.replace(/_/g, ' ').toUpperCase()} (${items.length})\n${items.join('\n')}`)
    .join('\n\n');
  const seedText = `[Codebase Analysis Summary — ${scopedNodes.length} nodes]\n\n${summary}\n\nBuild on this analysis: generate new ideas, features, and refinements grounded in what actually exists in the code.`;
  ctx.setManualMode('idea');
  ctx.setIdea(seedText);
  ctx.pushCard('feedToIdea', 'Bridged to Idea Mode', `${scopedNodes.length} nodes fed into idea mode`, []);
});

// --- Email Actions ---

registerAction('sendEmail', (params, ctx) => {
  const e = params;
  ctx.authFetch(`${ctx.API_URL}/api/integrations/gmail/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: e.to, subject: e.subject, body: e.body, cc: e.cc, bcc: e.bcc }),
  })
    .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error); }))
    .then(() => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'sendEmail', label: `Email sent to ${e.to}`, detail: e.subject, buttons: [], timestamp: Date.now() }]))
    .catch(err => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'sendEmail', label: 'Failed to send email', detail: err.message, buttons: [], timestamp: Date.now() }]));
});

registerAction('draftEmail', (params, ctx) => {
  const e = params;
  ctx.authFetch(`${ctx.API_URL}/api/integrations/gmail/draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: e.to, subject: e.subject, body: e.body, cc: e.cc, bcc: e.bcc }),
  })
    .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error); }))
    .then(() => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'draftEmail', label: `Draft created: ${e.subject}`, detail: `To: ${e.to || 'no recipient yet'}`, buttons: [], timestamp: Date.now() }]))
    .catch(err => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'draftEmail', label: 'Failed to create draft', detail: err.message, buttons: [], timestamp: Date.now() }]));
});

registerAction('replyToThread', (params, ctx) => {
  const e = params;
  const threadId = e.threadId || ctx.emailContext?.id;
  if (!threadId) {
    ctx.pushCard('replyToThread', 'No thread to reply to', 'Load an email thread first', []);
    return;
  }
  ctx.authFetch(`${ctx.API_URL}/api/integrations/gmail/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ threadId, body: e.body, cc: e.cc, bcc: e.bcc }),
  })
    .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error); }))
    .then(() => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'replyToThread', label: 'Reply sent', detail: `Thread: ${ctx.emailContext?.subject || threadId}`, buttons: [], timestamp: Date.now() }]))
    .catch(err => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'replyToThread', label: 'Failed to reply', detail: err.message, buttons: [], timestamp: Date.now() }]));
});

registerAction('searchEmail', (params, ctx) => {
  const q = params.query || '';
  const max = params.maxResults || 5;
  ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'searchEmail', label: 'Searching emails...', detail: q, buttons: [], timestamp: Date.now() }]);
  ctx.authFetch(`${ctx.API_URL}/api/integrations/gmail/threads?q=${encodeURIComponent(q)}&maxResults=${max}`)
    .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error); }))
    .then(data => {
      const threads = data.threads || [];
      if (threads.length === 0) {
        ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'searchEmail', label: 'No emails found', detail: `No results for "${q}"`, buttons: [], timestamp: Date.now() }]);
      } else {
        const summary = threads.map(t => `- **${t.subject || '(no subject)'}** from ${t.from || 'unknown'} (${t.messageCount || 1} messages)`).join('\n');
        ctx.setPendingChatCards(prev => [...prev, {
          type: 'action_card', action: 'searchEmail',
          label: `Found ${threads.length} thread${threads.length === 1 ? '' : 's'}`,
          detail: summary,
          buttons: threads.slice(0, 3).map(t => ({
            label: `Load: ${(t.subject || 'Thread').slice(0, 30)}`,
            onClick: () => ctx.gmail?.selectThread?.(t.id),
          })),
          timestamp: Date.now(),
        }]);
      }
    })
    .catch(err => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'searchEmail', label: 'Search failed', detail: err.message, buttons: [], timestamp: Date.now() }]);
    });
});

// --- Export ---

// Helper: push a Google artifact into CoworkPanel and auto-switch to Cowork mode
function pushGoogleArtifact(ctx, artifact) {
  if (ctx.setGoogleArtifacts) {
    ctx.setGoogleArtifacts(prev => [...prev, artifact]);
  }
  if (ctx.setViewLevel) {
    ctx.setViewLevel('cowork');
  }
}

registerAction('exportToGoogleDoc', (params, ctx) => {
  const { content, title } = params;
  ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleDoc', label: 'Exporting to Google Doc...', detail: title || 'Creating document', buttons: [], timestamp: Date.now() }]);
  ctx.authFetch(`${ctx.API_URL}/api/export/google-doc-content`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, title }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) throw new Error(result.error);
      pushGoogleArtifact(ctx, {
        id: `gdoc-${result.docId}`,
        type: 'google-doc',
        title: result.title || title || 'Google Doc',
        url: result.docUrl,
        previewUrl: result.docUrl?.replace(/\/edit$/, '/preview'),
        createdAt: new Date().toISOString(),
      });
      ctx.setPendingChatCards(prev => [...prev, {
        type: 'action_card', action: 'exportToGoogleDoc',
        label: 'Google Doc Created',
        detail: `${result.title} (${result.sectionCount} sections)`,
        buttons: result.docUrl ? [{ label: 'Open in Google', onClick: () => window.open(result.docUrl, '_blank') }] : [],
        timestamp: Date.now(),
      }]);
    })
    .catch(err => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleDoc', label: 'Export Failed', detail: err.message, buttons: [], timestamp: Date.now() }]);
    });
});

registerAction('exportToGoogleSlides', (params, ctx) => {
  const { content, title } = params;
  ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleSlides', label: 'Creating Google Slides...', detail: title || 'Building presentation', buttons: [], timestamp: Date.now() }]);
  ctx.authFetch(`${ctx.API_URL}/api/export/google-slides`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, title }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) throw new Error(result.error);
      pushGoogleArtifact(ctx, {
        id: `gslides-${result.presentationId}`,
        type: 'google-slides',
        title: result.title || title || 'Google Slides',
        url: result.presentationUrl,
        previewUrl: result.previewUrl,
        createdAt: new Date().toISOString(),
      });
      ctx.setPendingChatCards(prev => [...prev, {
        type: 'action_card', action: 'exportToGoogleSlides',
        label: 'Google Slides Created',
        detail: `${result.title} (${result.slideCount} slides)`,
        buttons: result.presentationUrl ? [{ label: 'Open in Google', onClick: () => window.open(result.presentationUrl, '_blank') }] : [],
        timestamp: Date.now(),
      }]);
    })
    .catch(err => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleSlides', label: 'Slides Export Failed', detail: err.message, buttons: [], timestamp: Date.now() }]);
    });
});

registerAction('exportToGoogleSheet', (params, ctx) => {
  const { content, title } = params;
  ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleSheet', label: 'Creating Google Sheet...', detail: title || 'Building spreadsheet', buttons: [], timestamp: Date.now() }]);
  ctx.authFetch(`${ctx.API_URL}/api/export/google-sheet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, title }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) throw new Error(result.error);
      pushGoogleArtifact(ctx, {
        id: `gsheet-${result.spreadsheetId}`,
        type: 'google-sheet',
        title: result.title || title || 'Google Sheet',
        url: result.spreadsheetUrl,
        previewUrl: result.previewUrl,
        createdAt: new Date().toISOString(),
      });
      ctx.setPendingChatCards(prev => [...prev, {
        type: 'action_card', action: 'exportToGoogleSheet',
        label: 'Google Sheet Created',
        detail: `${result.title} (${result.rowCount} rows)`,
        buttons: result.spreadsheetUrl ? [{ label: 'Open in Google', onClick: () => window.open(result.spreadsheetUrl, '_blank') }] : [],
        timestamp: Date.now(),
      }]);
    })
    .catch(err => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'exportToGoogleSheet', label: 'Sheet Export Failed', detail: err.message, buttons: [], timestamp: Date.now() }]);
    });
});

// --- Scheduling ---

registerAction('scheduleTask', (params, ctx) => {
  const st = params;
  const currentSessionId = ctx.gateway?.sessionId || ctx.initialSession?.id;
  const resolvedSessionId = (st.sessionId === 'CURRENT_SESSION' || st.sessionId === true) ? currentSessionId : (st.sessionId || currentSessionId);
  ctx.authFetch(`${ctx.API_URL}/api/tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: st.name || 'Scheduled task',
      type: st.type || 'custom',
      prompt: st.prompt || ctx.ideaText,
      mode: st.mode || ctx.displayMode,
      schedule: st.schedule || null,
      sessionId: resolvedSessionId,
      config: st.config || {},
    }),
  })
    .then(r => r.json())
    .then(task => {
      const desc = task.schedule?.cron ? ` (${task.schedule.cron})` : ' (manual)';
      const linked = task.sessionId ? ' (linked to session)' : '';
      const email = task.config?.emailTo ? ` → ${task.config.emailTo}` : '';
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'scheduleTask', label: `Scheduled: ${task.name}${desc}`, detail: `Type: ${task.type}${linked}${email}`, buttons: [], timestamp: Date.now() }]);
    })
    .catch(err => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'scheduleTask', label: 'Failed to schedule', detail: err.message, buttons: [], timestamp: Date.now() }]));
});

registerAction('listTasks', (_params, ctx) => {
  ctx.authFetch(`${ctx.API_URL}/api/tasks`)
    .then(r => r.json())
    .then(taskList => {
      if (!taskList?.length) {
        ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'listTasks', label: 'No scheduled tasks', detail: 'Create one via chat: "schedule a daily research task"', buttons: [], timestamp: Date.now() }]);
      } else {
        const detail = taskList.map(t => `\u2022 ${t.name} \u2014 ${t.scheduleDescription || 'manual'} (${t.lastRunStatus || 'never run'})`).join('\n');
        ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'listTasks', label: `${taskList.length} Scheduled Tasks`, detail, buttons: [], timestamp: Date.now() }]);
      }
    })
    .catch(() => {});
});

registerAction('runTask', (params, ctx) => {
  ctx.authFetch(`${ctx.API_URL}/api/tasks/${params.taskId}/run`, { method: 'POST' })
    .then(r => r.json())
    .then(result => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'runTask', label: 'Task Executed', detail: result.summary || result.error || 'Done', buttons: [], timestamp: Date.now() }]);
    })
    .catch(err => {
      ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'runTask', label: 'Task Failed', detail: err.message, buttons: [], timestamp: Date.now() }]);
    });
});

// ── Data Streams ────────────────────────────────────────────

registerAction('attachStream', (params, ctx) => {
  const { nodeId, type, url, interval } = params;
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) {
    ctx.pushCard('attachStream', 'Stream Error', 'No active session', []);
    return;
  }
  ctx.authFetch(`${ctx.API_URL}/api/streams/attach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, nodeId, type, url, interval }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) {
        ctx.pushCard('attachStream', 'Stream Error', result.error, []);
      } else {
        const detail = type === 'webhook'
          ? `Webhook URL: ${result.stream?.fullWebhookUrl || result.stream?.webhookUrl || 'generated'}`
          : `Monitoring ${url} every ${result.stream?.interval || interval || '1h'}`;
        ctx.pushCard('attachStream', 'Stream Attached', detail, []);
      }
    })
    .catch(err => {
      ctx.pushCard('attachStream', 'Stream Failed', err.message, []);
    });
});

registerAction('removeStream', (params, ctx) => {
  const { nodeId, streamId } = params;
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) return;
  ctx.authFetch(`${ctx.API_URL}/api/streams/remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, nodeId, streamId }),
  })
    .then(() => ctx.pushCard('removeStream', 'Stream Removed', 'Data stream disconnected', []))
    .catch(err => ctx.pushCard('removeStream', 'Remove Failed', err.message, []));
});

// ── Synapses ──────────────────────────────────────────────

registerAction('createSynapse', (params, ctx) => {
  const { source, target, type, weight, label } = params;
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) {
    ctx.pushCard('createSynapse', 'Synapse Error', 'No active session', []);
    return;
  }
  ctx.authFetch(`${ctx.API_URL}/api/synapses/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, source, target, type, weight, label }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) {
        ctx.pushCard('createSynapse', 'Synapse Error', result.error, []);
      } else {
        const sourceNode = ctx.active.rawNodesRef.current.find(n => n.id === source);
        const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === target);
        const srcLabel = sourceNode?.data?.label || source;
        const tgtLabel = targetNode?.data?.label || target;
        ctx.pushCard('createSynapse', `Synapse Created`, `${srcLabel} →[${type}]→ ${tgtLabel} (weight: ${weight || 0.5})`, []);
      }
    })
    .catch(err => ctx.pushCard('createSynapse', 'Synapse Failed', err.message, []));
});

registerAction('deleteSynapse', (params, ctx) => {
  const { synapseId } = params;
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) return;
  ctx.authFetch(`${ctx.API_URL}/api/synapses/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, synapseId }),
  })
    .then(() => ctx.pushCard('deleteSynapse', 'Synapse Removed', 'Connection deleted', []))
    .catch(err => ctx.pushCard('deleteSynapse', 'Delete Failed', err.message, []));
});

registerAction('suggestSynapses', (_params, ctx) => {
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) return;
  ctx.pushCard('suggestSynapses', 'Analyzing Tree...', 'Looking for cross-branch connections', []);
  ctx.authFetch(`${ctx.API_URL}/api/synapses/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  })
    .then(r => r.json())
    .then(result => {
      const suggestions = result.suggestions || [];
      if (suggestions.length === 0) {
        ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'suggestSynapses', label: 'No Connections Found', detail: 'The tree doesn\'t have obvious cross-branch relationships yet', buttons: [], timestamp: Date.now() }]);
      } else {
        const detail = suggestions.map(s => `• ${s.source} →[${s.type}]→ ${s.target}: ${s.rationale || s.label || ''}`).join('\n');
        ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'suggestSynapses', label: `${suggestions.length} Connections Suggested`, detail, buttons: [], timestamp: Date.now() }]);
      }
    })
    .catch(err => ctx.setPendingChatCards(prev => [...prev, { type: 'action_card', action: 'suggestSynapses', label: 'Suggestion Failed', detail: err.message, buttons: [], timestamp: Date.now() }]));
});

// ── Node Activation ───────────────────────────────────────

registerAction('setActivation', (params, ctx) => {
  const { nodeId, threshold, reactions, refractoryPeriod } = params;
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) {
    ctx.pushCard('setActivation', 'Activation Error', 'No active session', []);
    return;
  }
  ctx.authFetch(`${ctx.API_URL}/api/activation/set`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, nodeId, threshold, reactions, refractoryPeriod }),
  })
    .then(r => r.json())
    .then(result => {
      if (result.error) {
        ctx.pushCard('setActivation', 'Activation Error', result.error, []);
      } else {
        const targetNode = ctx.active.rawNodesRef.current.find(n => n.id === nodeId);
        const label = targetNode?.data?.label || nodeId;
        ctx.pushCard('setActivation', 'Activation Configured', `"${label}" will fire with: ${(reactions || ['notify']).join(', ')}`, []);
      }
    })
    .catch(err => ctx.pushCard('setActivation', 'Activation Failed', err.message, []));
});

registerAction('enableDreamState', (params, ctx) => {
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) return;
  const schedule = params?.schedule || '24h';
  ctx.authFetch(`${ctx.API_URL}/api/dream/enable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, schedule }),
  })
    .then(r => r.json())
    .then(() => ctx.pushCard('enableDreamState', 'Dream State Enabled', `Tree will dream every ${schedule}`, []))
    .catch(err => ctx.pushCard('enableDreamState', 'Dream State Failed', err.message, []));
});

registerAction('disableDreamState', (_params, ctx) => {
  const sessionId = ctx.sessionIdRef?.current;
  if (!sessionId) return;
  ctx.authFetch(`${ctx.API_URL}/api/dream/disable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  })
    .then(() => ctx.pushCard('disableDreamState', 'Dream State Disabled', 'Autonomous analysis stopped', []))
    .catch(err => ctx.pushCard('disableDreamState', 'Failed', err.message, []));
});
